import {
  DotsThreeVerticalIcon,
  DownloadSimpleIcon,
  ExportIcon,
  FloppyDiskBackIcon,
  GlobeSimpleIcon,
  SparkleIcon,
  SpinnerIcon,
  TrashIcon,
} from '@phosphor-icons/react'
import { memo } from 'react'
import type { Track } from './api.ts'

interface SaveFormProps {
  channelMode: 'single' | 'dual'
  user: { id: string } | null
  saveTitle: string
  effectiveSaveTitle: string
  saveButtonState: { isDisabled: boolean; hasChanges: boolean; isOwn: boolean }
  saving: boolean
  generatingTitle: boolean
  isExporting: boolean
  saveMenuOpen: boolean
  savePublic: boolean
  currentTrack: Track | null
  currentTrackId: string | undefined
  tracks: Track[]
  likedTracks: Track[]
  onSaveTitleChange: (value: string) => void
  onSaveTitleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onToggleSaveMenu: () => void
  onSave: () => void
  onSaveNew: () => void
  onGenerateTitle: () => void
  onExport: () => void
  onDownloadAllTracksZip: () => void
  onDeleteTrack: () => void
  onVisibilityToggle: (value: boolean) => void
  onSaveAsRemix: () => void
  onShowAuthModal: () => void
  onSetExportBpm: (bpm: number) => void
  onSetShowExportModal: (show: boolean) => void
  onSetError: (error: string) => void
}

export const SaveForm = memo(
  ({
    channelMode,
    user,
    saveTitle,
    effectiveSaveTitle,
    saveButtonState,
    saving,
    generatingTitle,
    isExporting,
    saveMenuOpen,
    savePublic,
    currentTrack,
    currentTrackId,
    tracks,
    likedTracks,
    onSaveTitleChange,
    onSaveTitleKeyDown,
    onToggleSaveMenu,
    onSave,
    onSaveNew,
    onGenerateTitle,
    onExport,
    onDownloadAllTracksZip,
    onDeleteTrack,
    onVisibilityToggle,
    onSaveAsRemix,
    onShowAuthModal,
    onSetExportBpm,
    onSetShowExportModal,
    onSetError,
  }: SaveFormProps) => {
    if (channelMode === 'dual') {
      return null
    }

    const remixTrack = currentTrackId
      ? (currentTrack
        || tracks.find(t => t.id === currentTrackId)
        || likedTracks.find(t => t.id === currentTrackId))
      : null
    const remixTitle = remixTrack
      ? `Save as Remix of ${remixTrack.userName || 'Unknown'} - ${remixTrack.title}`
      : 'Save as Remix'

    return (
      <form className="p-4 border-b border-[#333] shrink-0" onSubmit={e => e.preventDefault()}>
        <div className="space-y-2">
          <div className="flex gap-2 items-center">
            {currentTrackId
              && saveButtonState.isOwn
              && (
                <div>
                  <button
                    type="button"
                    disabled={saveButtonState.isDisabled}
                    title={saveButtonState.isDisabled ? 'No changes to save' : 'Save'}
                    onClick={!user ? onShowAuthModal : onSave}
                    className={`w-full px-3 py-2 text-white rounded-md hover:opacity-90 disabled:opacity-50 text-sm flex items-center gap-2 ${
                      saveButtonState.isDisabled
                        ? 'bg-gradient-to-br from-neutral-700 to-black'
                        : 'bg-gradient-to-br from-blue-600 to-blue-800'
                    }`}
                  >
                    {saving
                      ? <SpinnerIcon weight="regular" size={16} className="animate-spin" />
                      : <FloppyDiskBackIcon weight="regular" size={16} />}
                  </button>
                </div>
              )}
            <input
              type="text"
              placeholder="Loop title"
              title="Loop title"
              value={effectiveSaveTitle}
              onChange={e => onSaveTitleChange(e.target.value)}
              onKeyDown={onSaveTitleKeyDown}
              className="flex-1 px-3 h-7 bg-gradient-to-br from-neutral-900 to-neutral-700 border border-none rounded text-white text-sm"
              required
            />
            <button
              type="button"
              onClick={onToggleSaveMenu}
              className={`px-1 py-2 rounded text-sm hover:opacity-90 flex items-center text-white ${
                saveMenuOpen
                  ? 'bg-gradient-to-br from-black to-neutral-800'
                  : 'bg-gradient-to-br from-neutral-700 to-neutral-900'
              }`}
              title="More options"
            >
              <DotsThreeVerticalIcon weight="bold" size={16} />
            </button>
          </div>
          {((!user && currentTrackId) || !saveButtonState.isOwn || !currentTrackId)
            && (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  title={currentTrack ? 'Save as new loop' : 'Save'}
                  disabled={saving || saveButtonState.isDisabled}
                  onClick={e => {
                    if (saving || saveButtonState.isDisabled) return
                    if (!user) {
                      onShowAuthModal()
                    }
                    else {
                      onSaveNew()
                    }
                  }}
                  className={`px-3 py-1 w-full justify-center text-white rounded-md hover:opacity-90 disabled:opacity-50 text-sm flex items-center gap-2 ${
                    saveButtonState.isDisabled
                      ? 'bg-gradient-to-br from-neutral-700 to-black'
                      : 'bg-gradient-to-br from-blue-600 to-blue-800'
                  }`}
                >
                  <FloppyDiskBackIcon weight="regular" size={16} />
                  <span>{saving ? 'Saving...' : currentTrack ? 'Save as New' : 'Save'}</span>
                </button>
                {remixTrack && (
                  <button
                    type="button"
                    title={remixTitle}
                    disabled={saving || saveButtonState.isDisabled}
                    onClick={!user ? onShowAuthModal : onSaveAsRemix}
                    className={`px-3 py-1 w-full justify-center text-white rounded-md hover:opacity-90 disabled:opacity-50 text-sm flex items-center gap-2 ${
                      saveButtonState.isDisabled
                        ? 'bg-gradient-to-br from-neutral-700 to-black'
                        : 'bg-gradient-to-br from-orange-600 to-orange-800'
                    }`}
                  >
                    <FloppyDiskBackIcon weight="regular" size={16} />
                    <span className="truncate">{saving ? 'Saving...' : remixTitle}</span>
                  </button>
                )}
              </div>
            )}
          {saveMenuOpen && (
            <div className="flex flex-col items-start border border-[#444] p-2 space-y-2">
              {user && saveButtonState.isOwn && (
                <>
                  <div className="flex flex-col gap-2 w-full">
                    <button
                      type="button"
                      title={currentTrack ? 'Save as new loop' : 'Save'}
                      disabled={saving || saveButtonState.isDisabled}
                      onClick={onSaveNew}
                      className={`px-3 py-1 w-full justify-center text-white rounded-md hover:opacity-90 disabled:opacity-50 text-sm flex items-center gap-2 ${
                        saveButtonState.isDisabled
                          ? 'bg-gradient-to-br from-neutral-700 to-black'
                          : 'bg-gradient-to-br from-blue-600 to-blue-800'
                      }`}
                    >
                      <FloppyDiskBackIcon weight="regular" size={16} />
                      <span>{saving ? 'Saving...' : currentTrack ? 'Save as New' : 'Save'}</span>
                    </button>
                    {remixTrack && (
                      <button
                        type="button"
                        title={remixTitle}
                        disabled={saving || saveButtonState.isDisabled}
                        onClick={onSaveAsRemix}
                        className={`px-3 py-1 w-full justify-center text-white rounded-md hover:opacity-90 disabled:opacity-50 text-sm flex items-center gap-2 ${
                          saveButtonState.isDisabled
                            ? 'bg-gradient-to-br from-neutral-700 to-black'
                            : 'bg-gradient-to-br from-orange-600 to-orange-800'
                        }`}
                      >
                        <FloppyDiskBackIcon weight="regular" size={16} />
                        <span className="truncate">{saving ? 'Saving...' : remixTitle}</span>
                      </button>
                    )}
                  </div>
                  <div className="w-full border-b border-[#444]" />
                  <div className="flex gap-2 items-center">
                    <label
                      title="Visibility"
                      className="inline-flex items-center cursor-pointer gap-2 text-xs"
                    >
                      Visibility:
                      <input
                        type="checkbox"
                        checked={savePublic}
                        onChange={e => onVisibilityToggle(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          savePublic
                            ? 'bg-gradient-to-br from-green-600 to-lime-400'
                            : 'bg-gradient-to-br from-neutral-900 to-neutral-700'
                        }`}
                      >
                        <div
                          className={`absolute top-[2px] bg-white text-neutral-500 border border-gray-300 rounded-full w-5 h-5 transition-transform duration-200 flex items-center justify-center ${
                            savePublic
                              ? 'left-[2px] translate-x-[24px]'
                              : 'right-[2px] translate-x-[-24px]'
                          }`}
                        >
                          <GlobeSimpleIcon weight="regular" size={16} />
                        </div>
                      </div>
                      <span className="ms-3 text-sm text-white flex items-center gap-2">
                        <span>{savePublic ? 'Public' : 'Private'}</span>
                      </span>
                    </label>
                  </div>
                  <div className="w-full border-b border-[#444]" />
                </>
              )}
              <button
                type="button"
                onClick={onGenerateTitle}
                disabled={generatingTitle}
                className="px-3 py-1 w-full bg-gradient-to-br from-pink-500 to-fuchsia-700 text-white rounded-md hover:opacity-90 disabled:opacity-50 text-sm flex items-center justify-center gap-2"
              >
                {generatingTitle
                  ? (
                    <>
                      <SpinnerIcon weight="regular" size={16} className="animate-spin" />
                      <span>Generating...</span>
                    </>
                  )
                  : (
                    <>
                      <SparkleIcon weight="regular" size={16} />
                      <span>Generate Title with AI</span>
                    </>
                  )}
              </button>
              <div className="w-full border-b border-[#444]" />
              <button
                type="button"
                onClick={onExport}
                disabled={isExporting}
                className="px-3 py-1 w-full justify-center bg-white text-black rounded-md hover:opacity-90 disabled:opacity-50 text-sm flex items-center gap-2"
              >
                {isExporting
                  ? (
                    <>
                      <SpinnerIcon weight="regular" size={16} className="animate-spin" />
                      <span>Processing...</span>
                    </>
                  )
                  : (
                    <>
                      <ExportIcon weight="regular" size={16} />
                      <span>Export Audio</span>
                    </>
                  )}
              </button>
              {user && tracks.length > 0 && (
                <>
                  <div className="w-full border-b border-[#444]" />
                  <button
                    type="button"
                    onClick={onDownloadAllTracksZip}
                    className="px-3 py-1 w-full justify-center bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-md hover:opacity-90 text-sm flex items-center gap-2"
                  >
                    <DownloadSimpleIcon weight="regular" size={16} />
                    <span>Download All Loops Zip</span>
                  </button>
                </>
              )}
              {currentTrackId
                && (currentTrack
                  || tracks.find(t => t.id === currentTrackId)
                  || likedTracks.find(t => t.id === currentTrackId))
                && user
                && (currentTrack?.userId === user.id
                  || tracks.find(t => t.id === currentTrackId)?.userId === user.id)
                && (
                  <>
                    <div className="w-full border-b border-[#444]" />
                    <button
                      type="button"
                      onClick={onDeleteTrack}
                      className="px-3 py-1 w-full justify-center bg-gradient-to-br from-red-600 to-red-800 text-white rounded-md hover:opacity-90 text-sm flex items-center gap-2"
                    >
                      <TrashIcon weight="regular" size={16} />
                      <span>Delete Loop</span>
                    </button>
                  </>
                )}
            </div>
          )}
        </div>
      </form>
    )
  },
  (prevProps, nextProps) => {
    // Only rerender if these props actually change
    return (
      prevProps.channelMode === nextProps.channelMode
      && prevProps.user === nextProps.user
      && prevProps.saveTitle === nextProps.saveTitle
      && prevProps.effectiveSaveTitle === nextProps.effectiveSaveTitle
      && prevProps.saveButtonState.isDisabled === nextProps.saveButtonState.isDisabled
      && prevProps.saveButtonState.hasChanges === nextProps.saveButtonState.hasChanges
      && prevProps.saveButtonState.isOwn === nextProps.saveButtonState.isOwn
      && prevProps.saving === nextProps.saving
      && prevProps.generatingTitle === nextProps.generatingTitle
      && prevProps.isExporting === nextProps.isExporting
      && prevProps.saveMenuOpen === nextProps.saveMenuOpen
      && prevProps.savePublic === nextProps.savePublic
      && prevProps.currentTrack === nextProps.currentTrack
      && prevProps.currentTrackId === nextProps.currentTrackId
      && prevProps.tracks === nextProps.tracks
      && prevProps.likedTracks === nextProps.likedTracks
    )
  },
)

SaveForm.displayName = 'SaveForm'
